'use client';

import React, { useCallback, useState } from 'react';
import { Modal, Upload, Button, Input, message, Progress } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import {
  uploadCommunityImage,
  savePendingPhotos,
} from '../utils/reviewerApi';
import { resizeImage } from '../utils/imageResize';
import { useReviewerAuth } from '../contexts/ReviewerAuthContext';
import ReviewerLoginPrompt from './ReviewerLoginPrompt';

const { Dragger } = Upload;

// ── Props ─────────────────────────────────────────────────────

interface CommunityPhotoUploadProps {
  entityId: number;
  entityName: string;
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

// ── Constants ─────────────────────────────────────────────────

const MAX_FILES = 5;
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

// ── Component ─────────────────────────────────────────────────

const CommunityPhotoUpload: React.FC<CommunityPhotoUploadProps> = ({
  entityId,
  entityName,
  isOpen,
  onClose,
  onUploadSuccess,
}) => {
  const { isLoggedIn } = useReviewerAuth();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // ── File validation ──────────────────────────────────────────

  const beforeUpload = useCallback(
    (file: RcFile): boolean | string => {
      const ext = file.name?.split('.').pop()?.toLowerCase() || '';
      const isHeic = ext === 'heic' || ext === 'heif';
      if (!ACCEPTED_TYPES.includes(file.type) && !isHeic) {
        message.error(`${file.name} is not a supported image format (JPG, PNG, WEBP, HEIC).`);
        return Upload.LIST_IGNORE;
      }

      if (file.size > MAX_SIZE_BYTES) {
        message.error(`${file.name} exceeds the ${MAX_SIZE_MB}MB size limit.`);
        return Upload.LIST_IGNORE;
      }

      if (fileList.length >= MAX_FILES) {
        message.warning(`You can upload a maximum of ${MAX_FILES} photos at a time.`);
        return Upload.LIST_IGNORE;
      }

      return false; // Prevent auto-upload; we handle it manually
    },
    [fileList.length],
  );

  // ── File list management ─────────────────────────────────────

  const handleChange = useCallback(
    ({ fileList: newList }: { fileList: UploadFile[] }) => {
      setFileList(newList.slice(0, MAX_FILES));
    },
    [],
  );

  const handleRemove = useCallback(
    (file: UploadFile) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
      setCaptions((prev) => {
        const next = { ...prev };
        delete next[file.uid];
        return next;
      });
    },
    [],
  );

  const handleCaptionChange = useCallback(
    (uid: string, value: string) => {
      setCaptions((prev) => ({ ...prev, [uid]: value }));
    },
    [],
  );

  // ── Upload logic ─────────────────────────────────────────────

  const uploadFiles = useCallback(async () => {
    if (fileList.length === 0) {
      message.warning('Please select at least one photo.');
      return;
    }

    if (!isLoggedIn) {
      // Save captions to sessionStorage so they survive the OAuth redirect
      const captionList = fileList.map((f) => captions[f.uid] || '');
      savePendingPhotos({ entityId, captions: captionList });
      setShowLoginPrompt(true);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const total = fileList.length;
    let completed = 0;
    let failed = 0;

    for (const file of fileList) {
      const rawFile = file.originFileObj;
      if (!rawFile) {
        failed++;
        completed++;
        setUploadProgress(Math.round((completed / total) * 100));
        continue;
      }

      try {
        // 0. Resize and convert HEIC→JPEG if needed
        const processed = await resizeImage(rawFile);

        // 1. Direct multipart upload — server writes to disk + creates
        //    the CommunityImage Neo4j node + returns the record. Build
        //    244 #3 replaces the legacy 3-step S3 presigned-URL flow
        //    (the S3 bucket `client.trip-planner.com.au` was removed
        //    post-Hetzner migration so the old flow has been silently
        //    failing since the move).
        await uploadCommunityImage(
          entityId,
          processed,
          captions[file.uid] || undefined,
        );

        completed++;
        setUploadProgress(Math.round((completed / total) * 100));
      } catch (err) {
        console.error(`[CommunityPhotoUpload] Failed to upload ${rawFile.name}:`, err);
        failed++;
        completed++;
        setUploadProgress(Math.round((completed / total) * 100));
      }
    }

    setUploading(false);

    if (failed === 0) {
      message.success("Photos submitted! They'll appear once approved.");
      setFileList([]);
      setCaptions({});
      onUploadSuccess?.();
      onClose();
    } else if (failed < total) {
      message.warning(
        `${total - failed} photo(s) uploaded. ${failed} failed — please try those again.`,
      );
    } else {
      message.error('All uploads failed. Please check your connection and try again.');
    }
  }, [fileList, captions, isLoggedIn, entityId, onUploadSuccess, onClose]);

  // ── Reset on close ───────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (uploading) return; // Prevent closing mid-upload
    setFileList([]);
    setCaptions({});
    setUploadProgress(0);
    onClose();
  }, [uploading, onClose]);

  // ── Render ───────────────────────────────────────────────────

  return (
    <>
      <Modal
        title={`Share Photos of ${entityName}`}
        open={isOpen}
        onCancel={handleClose}
        footer={null}
        width={560}
        destroyOnClose
        maskClosable={!uploading}
        closable={!uploading}
      >
        {/* Drag-and-drop upload zone */}
        <Dragger
          multiple
          accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
          fileList={fileList}
          beforeUpload={beforeUpload}
          onChange={handleChange}
          onRemove={handleRemove}
          listType="picture"
          showUploadList={false}
          disabled={uploading}
          className="mb-4"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag photos here to upload
          </p>
          <p className="ant-upload-hint">
            JPG, PNG, WEBP, or HEIC. Max {MAX_FILES} files, {MAX_SIZE_MB}MB each.
          </p>
        </Dragger>

        {/* Selected file previews with captions */}
        {fileList.length > 0 && (
          <div className="mt-4 space-y-3">
            {fileList.map((file) => {
              const thumbUrl =
                file.thumbUrl ||
                (file.originFileObj
                  ? URL.createObjectURL(file.originFileObj)
                  : undefined);

              return (
                <div
                  key={file.uid}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  {/* Thumbnail */}
                  {thumbUrl && (
                    <div className="flex-shrink-0 w-16 h-16 rounded overflow-hidden bg-gray-200">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={thumbUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Details + caption */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate mb-1">
                      {file.name}
                    </p>
                    <Input
                      placeholder="Add a caption (optional)"
                      size="small"
                      value={captions[file.uid] || ''}
                      onChange={(e) =>
                        handleCaptionChange(file.uid, e.target.value)
                      }
                      disabled={uploading}
                      maxLength={200}
                    />
                  </div>

                  {/* Remove button */}
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(file)}
                    disabled={uploading}
                    className="flex-shrink-0 mt-1"
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Upload progress */}
        {uploading && (
          <div className="mt-4">
            <Progress percent={uploadProgress} status="active" />
            <p className="text-xs text-gray-500 mt-1 text-center">
              Uploading photos...
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={uploadFiles}
            loading={uploading}
            disabled={fileList.length === 0}
          >
            {isLoggedIn ? 'Upload Photos' : 'Upload Photos'}
          </Button>
        </div>

        {/* Auth note for logged-out users */}
        {!isLoggedIn && fileList.length > 0 && (
          <p className="text-xs text-amber-600 text-center mt-2">
            You&apos;ll be asked to sign in before your photos are submitted.
            After signing in you may need to re-select your files.
          </p>
        )}
      </Modal>

      {/* Login overlay */}
      <ReviewerLoginPrompt
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in to upload photos"
        subtitle="We need to know who's sharing — sign in to continue."
      />
    </>
  );
};

export default CommunityPhotoUpload;
